/*
 * Virtual Try-On (KSJ) — browser-based face-overlay AR try-on.
 *
 * Self-contained custom element <virtual-tryon>. Uses Google MediaPipe
 * Face Landmarker (loaded lazily from CDN only on first open, so it adds
 * no weight to normal page loads). Draws the camera feed + a transparent
 * jewellery cutout onto a canvas, anchored to face landmarks, with manual
 * Width / Up-Down / Left-Right fine-tuning.
 *
 * Nothing here runs unless the snippet rendered the element, which only
 * happens for products tagged `tryon`.
 */
(function () {
  if (customElements.get('virtual-tryon')) return;

  var VISION_VER = '0.10.14';
  var CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + VISION_VER;
  var WASM_URL = CDN + '/wasm';
  var MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  // One shared landmarker for the whole page, created on first use.
  var landmarkerPromise = null;
  function getLandmarker() {
    if (!landmarkerPromise) {
      landmarkerPromise = (async function () {
        var vision = await import(/* @vite-ignore */ CDN);
        var resolver = await vision.FilesetResolver.forVisionTasks(WASM_URL);
        return vision.FaceLandmarker.createFromOptions(resolver, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numFaces: 1,
        });
      })();
    }
    return landmarkerPromise;
  }

  class VirtualTryon extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-tryon-open]');
      this.modal = this.querySelector('[data-tryon-modal]');
      if (!this.trigger || !this.modal) return;

      // Hoist the modal to <body> so it overlays everything regardless of
      // ancestor overflow/transform/stacking contexts.
      document.body.appendChild(this.modal);

      this.video = this.modal.querySelector('[data-tryon-video]');
      this.canvas = this.modal.querySelector('[data-tryon-canvas]');
      this.ctx = this.canvas.getContext('2d');
      this.statusEl = this.modal.querySelector('[data-tryon-status]');
      this.badge = this.modal.querySelector('[data-tryon-badge]');
      this.cameraBtn = this.modal.querySelector('[data-tryon-camera]');

      this.type = (this.dataset.tryonType || 'necklace').toLowerCase();
      this.adjust = { width: 1, x: 0, y: 0 };

      var self = this;
      this.overlay = new Image();
      this.overlay.crossOrigin = 'anonymous';
      this.overlay.onload = function () { self.overlayReady = true; };
      this.overlayReady = false;
      if (this.dataset.tryonImage) this.setOverlay(this.dataset.tryonImage);

      // Image picker: thumbnail opens a grid of all product images so the
      // client can switch the overlay (e.g. pick the transparent PNG) live.
      this.thumbBtn = this.modal.querySelector('[data-tryon-thumb-btn]');
      this.thumbImg = this.modal.querySelector('[data-tryon-thumb-img]');
      this.picker = this.modal.querySelector('[data-tryon-picker]');
      if (this.thumbBtn) this.thumbBtn.addEventListener('click', function () { self.togglePicker(); });
      var pickerClose = this.modal.querySelector('[data-tryon-picker-close]');
      if (pickerClose) pickerClose.addEventListener('click', function () { self.closePicker(); });
      this.pickerItems = this.modal.querySelectorAll('[data-pick-src]');
      this.pickerItems.forEach(function (item) {
        item.addEventListener('click', function () {
          self.setOverlay(item.dataset.pickSrc);
          if (self.thumbImg && item.dataset.pickThumb) self.thumbImg.src = item.dataset.pickThumb;
          self.pickerItems.forEach(function (i) { i.classList.remove('is-active'); });
          item.classList.add('is-active');
          self.closePicker();
        });
        if (self.dataset.tryonImage && item.dataset.pickSrc === self.dataset.tryonImage) {
          item.classList.add('is-active');
        }
      });

      this.running = false;
      this.lastVideoTime = -1;
      this._landmarks = null;

      this.trigger.addEventListener('click', function () { self.open(); });
      this.modal.querySelectorAll('[data-tryon-close]').forEach(function (b) {
        b.addEventListener('click', function () { self.close(); });
      });
      this.modal.addEventListener('click', function (e) {
        if (e.target === self.modal) self.close();
      });
      if (this.cameraBtn) {
        this.cameraBtn.addEventListener('click', function () {
          self.running ? self.stopCamera() : self.startCamera();
        });
      }
      var resetBtn = this.modal.querySelector('[data-tryon-reset]');
      if (resetBtn) resetBtn.addEventListener('click', function () { self.resetAdjust(); });
      this.modal.querySelectorAll('[data-adjust]').forEach(function (input) {
        input.addEventListener('input', function (e) {
          self.adjust[e.target.dataset.adjust] = parseFloat(e.target.value);
        });
      });
      var snap = this.modal.querySelector('[data-tryon-snap]');
      if (snap) snap.addEventListener('click', function () { self.snapshot(); });

      this._esc = function (e) { if (e.key === 'Escape') self.close(); };
    }

    open() {
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this._esc);
      this.startCamera();
    }

    close() {
      this.stopCamera();
      this.closePicker();
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', this._esc);
    }

    setStatus(msg, spinner) {
      if (!this.statusEl) return;
      if (!msg) { this.statusEl.hidden = true; this.statusEl.innerHTML = ''; return; }
      this.statusEl.hidden = false;
      this.statusEl.innerHTML = (spinner ? '<span class="vto__spinner"></span>' : '') + msg;
    }

    async startCamera() {
      if (this.running) return;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.setStatus('Camera is not supported on this browser.');
        return;
      }
      this.setStatus('Starting camera…', true);
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (err) {
        this.setStatus('Camera access was blocked. Please allow camera permission and try again.');
        return;
      }
      this.video.srcObject = this.stream;
      try { await this.video.play(); } catch (e) {}

      this.setStatus('Loading try-on engine…', true);
      try {
        this.landmarker = await getLandmarker();
      } catch (err) {
        this.setStatus('Could not load the try-on engine. Check your connection and retry.');
        this.stopCamera();
        return;
      }

      this.setStatus('');
      this.running = true;
      this._setCamLabel(true);
      this.loop();
    }

    stopCamera() {
      this.running = false;
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this.stream) {
        this.stream.getTracks().forEach(function (t) { t.stop(); });
        this.stream = null;
      }
      if (this.video) this.video.srcObject = null;
      this._landmarks = null;
      if (this.badge) this.badge.hidden = true;
      if (this.ctx && this.canvas) this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this._setCamLabel(false);
    }

    _setCamLabel(on) {
      if (!this.cameraBtn) return;
      this.cameraBtn.textContent = on
        ? (this.cameraBtn.dataset.stopLabel || 'Stop Camera')
        : (this.cameraBtn.dataset.startLabel || 'Start Camera');
      this.cameraBtn.classList.toggle('is-on', !!on);
    }

    resetAdjust() {
      this.adjust = { width: 1, x: 0, y: 0 };
      this.modal.querySelectorAll('[data-adjust]').forEach(function (i) {
        i.value = i.dataset.default != null ? i.dataset.default : (i.dataset.adjust === 'width' ? '1' : '0');
      });
    }

    setOverlay(src) {
      if (!src) return;
      this.overlayReady = false;
      this.overlay.src = src;
    }

    togglePicker() {
      if (this.picker) this.picker.hidden = !this.picker.hidden;
    }

    closePicker() {
      if (this.picker) this.picker.hidden = true;
    }

    loop() {
      if (!this.running) return;
      var v = this.video;
      var self = this;
      if (v && v.readyState >= 2 && v.videoWidth) {
        if (this.canvas.width !== v.videoWidth) {
          this.canvas.width = v.videoWidth;
          this.canvas.height = v.videoHeight;
        }
        if (this.landmarker && v.currentTime !== this.lastVideoTime) {
          this.lastVideoTime = v.currentTime;
          try {
            var res = this.landmarker.detectForVideo(v, performance.now());
            this._landmarks = res && res.faceLandmarks && res.faceLandmarks[0] ? res.faceLandmarks[0] : null;
          } catch (e) { /* transient detection error — keep last landmarks */ }
        }
        var ctx = this.ctx, w = this.canvas.width, h = this.canvas.height;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(v, 0, 0, w, h);
        if (this._landmarks && this.overlayReady) this.drawOverlay(ctx, this._landmarks, w, h);
        if (this.badge) this.badge.hidden = !this._landmarks;
      }
      this._raf = requestAnimationFrame(function () { self.loop(); });
    }

    drawOverlay(ctx, lm, w, h) {
      var img = this.overlay;
      var ar = img.naturalWidth ? img.naturalHeight / img.naturalWidth : 1;
      var a = this.adjust;
      function pt(i) { return { x: lm[i].x * w, y: lm[i].y * h }; }

      var left = pt(234), right = pt(454); // face left/right boundary
      var faceW = Math.hypot(right.x - left.x, right.y - left.y);
      var cx = (left.x + right.x) / 2;

      if (this.type === 'tikka' || this.type === 'maangtikka' || this.type === 'forehead') {
        var fhead = pt(10); // top-centre of forehead
        var twW = faceW * 0.55 * a.width;
        ctx.drawImage(img, cx - twW / 2 + a.x, fhead.y - twW * ar * 0.45 + a.y, twW, twW * ar);
      } else if (this.type === 'earring' || this.type === 'earrings') {
        var lEar = pt(132), rEar = pt(361); // near the two lobes
        var eW = faceW * 0.18 * a.width, eH = eW * ar;
        ctx.drawImage(img, rEar.x - eW / 2 + a.x, rEar.y + a.y, eW, eH); // right lobe
        ctx.save();
        ctx.translate(lEar.x + a.x, lEar.y + a.y);
        ctx.scale(-1, 1); // mirror the cutout for the opposite ear
        ctx.drawImage(img, -eW / 2, 0, eW, eH);
        ctx.restore();
      } else { // necklace (default)
        var chin = pt(152);
        var nW = faceW * 1.7 * a.width, nH = nW * ar;
        ctx.drawImage(img, cx - nW / 2 + a.x, chin.y + faceW * 0.15 + a.y, nW, nH);
      }
    }

    snapshot() {
      if (!this.canvas.width) return;
      try {
        var out = document.createElement('canvas');
        out.width = this.canvas.width;
        out.height = this.canvas.height;
        var o = out.getContext('2d');
        o.translate(out.width, 0);
        o.scale(-1, 1); // un-mirror so the saved photo matches what the user sees
        o.drawImage(this.canvas, 0, 0);
        var a = document.createElement('a');
        a.href = out.toDataURL('image/png');
        a.download = ((this.dataset.productTitle || 'try-on').replace(/\s+/g, '-').toLowerCase()) + '-tryon.png';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        this.setStatus('Could not save the photo on this device.');
      }
    }
  }

  customElements.define('virtual-tryon', VirtualTryon);
})();
